import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, Fragment as _Fragment } from "vue"

const _hoisted_1 = { "data-cy": "conflict-saved-check" }
const _hoisted_2 = { class: "mt-4 flex flex-wrap items-center gap-3 print:hidden" }
const _hoisted_3 = {
  key: 1,
  class: "mt-4 rounded border border-gray-300 bg-gray-50 px-3.5 py-3 text-xs leading-relaxed",
  "data-cy": "conflict-saved-not-found"
}
const _hoisted_4 = {
  key: 2,
  class: "mt-4 rounded border border-l-4 border-[rgba(var(--v-theme-error),0.3)] bg-[rgba(var(--v-theme-error),0.08)] px-3.5 py-3 text-xs leading-relaxed",
  "data-cy": "conflict-saved-error"
}
const _hoisted_5 = { class: "mt-2.5" }
const _hoisted_6 = {
  class: "mt-4 rounded border border-gray-300 bg-gray-50 print:!mt-0 print:!border-0 print:bg-white",
  "aria-labelledby": "conflict-saved-heading",
  "data-cy": "conflict-saved-record"
}
const _hoisted_7 = { class: "flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-gray-300 px-4 py-3 print:hidden" }
const _hoisted_8 = {
  class: "text-xs text-gray-600",
  "data-cy": "conflict-saved-run"
}
const _hoisted_9 = { class: "text-xs text-gray-600" }
const _hoisted_10 = { class: "px-4 pb-4 print:!p-0" }
const _hoisted_11 = { class: "mt-3 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1 text-xs print:hidden" }
const _hoisted_12 = { class: "text-gray-800 [overflow-wrap:anywhere]" }
const _hoisted_13 = { class: "text-gray-500" }
const _hoisted_14 = { class: "text-gray-800 [overflow-wrap:anywhere]" }
const _hoisted_15 = {
  key: 0,
  class: "mt-4 rounded border border-amber-200 bg-amber-50 px-3.5 py-3 text-xs"
}
const _hoisted_16 = {
  key: 1,
  class: "mt-4 rounded border border-green-200 bg-green-50 px-3.5 py-3 text-xs leading-relaxed",
  "data-cy": "conflict-saved-clean"
}
const _hoisted_17 = {
  key: 3,
  class: "mt-4 border-t border-gray-300 pt-3",
  "data-cy": "conflict-saved-notes-fixed"
}
const _hoisted_18 = {
  key: 0,
  class: "mt-1 whitespace-pre-line text-sm text-gray-800 [overflow-wrap:anywhere]"
}
const _hoisted_19 = {
  key: 1,
  class: "mt-1 text-sm italic text-gray-600"
}
const _hoisted_20 = {
  class: "mt-2 text-xs text-gray-500 print:hidden",
  "data-cy": "conflict-saved-notes-lock"
}
const _hoisted_21 = {
  key: 0,
  class: "font-medium"
}
const _hoisted_22 = {
  key: 0,
  class: "mt-4 rounded border border-gray-200 bg-white px-4 py-3 print:!border-0 print:!px-0",
  "aria-labelledby": "conflict-saved-notes-heading",
  "data-cy": "conflict-saved-notes-open"
}
const _hoisted_23 = { class: "flex flex-wrap items-baseline gap-x-3 gap-y-1" }
const _hoisted_24 = {
  class: "text-xs text-gray-500 print:hidden",
  "data-cy": "conflict-saved-notes-lock"
}
const _hoisted_25 = {
  key: 0,
  class: "ml-auto print:hidden"
}
const _hoisted_26 = {
  key: 0,
  class: "mt-2 print:hidden"
}
const _hoisted_27 = { class: "mt-3 flex gap-3" }
const _hoisted_28 = {
  key: 0,
  class: "mt-1 whitespace-pre-line text-sm text-gray-800 [overflow-wrap:anywhere]"
}
const _hoisted_29 = {
  key: 1,
  class: "mt-1 text-sm italic text-gray-600"
}

import { computed, reactive, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useMeStore } from '@/services/UserService/useMeStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import LlIcon from '@/components/LlIcon.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import ConflictNotesField from './ConflictNotesField.vue';
import ConflictPrintMasthead from './ConflictPrintMasthead.vue';
import ConflictResultList from './ConflictResultList.vue';
import { useConflictCheckRecordStore } from '../stores/useConflictCheckRecordStore';
import { CONFLICT_SCOPE_SENTENCE } from '../constants/conflictFieldConstants';
import { CONFLICT_NOTES_MAX_LENGTH } from '../constants/conflictRecordConstants';
import { formatRunAt, formatRunDate } from '../utils/conflictDateHelpers';
import type {
  ConflictPrintSheet,
  ConflictResultRow,
} from '../models/conflictTypes';


export default /*@__PURE__*/_defineComponent({
  __name: 'SavedConflictCheckView',
  emits: ["back", "retry", "print", "open", "dirty"],
  setup(__props, { expose: __expose, emit: __emit }) {

const emit = __emit;

const recordStore = useConflictCheckRecordStore();
const { record } = storeToRefs(recordStore);
const { me } = storeToRefs(useMeStore());
const { handleError, handleSuccess } = useSnackbarHandler();

const uiState = reactive({ editing: false, draft: '' });
const resultList = ref<InstanceType<typeof ConflictResultList> | null>(null);

const runAt = computed(() => formatRunAt(record.value?.runAtUtc));
const lockDate = computed(() => formatRunDate(record.value?.notesLockAtUtc));
const scopeTail = computed(() =>
  record.value?.includedTimeEntries
    ? ', and time entry descriptions.'
    : '. Time entries were not searched.',
);
const draftOverLimit = computed(
  () => uiState.draft.length > CONFLICT_NOTES_MAX_LENGTH,
);

/**
 * Why these notes cannot change, in ERR-011/012's words. The server's refusal wins;
 * otherwise the lock date having passed tells a closed window from a non-author.
 */
const fixedNotesReason = computed(() => {
  const current = record.value;
  if (!current) return '';
  const locked = recordStore.notesRefusal
    ? recordStore.notesRefusal.reason === 'windowClosed'
    : new Date(current.notesLockAtUtc).getTime() <= Date.now();
  return locked
    ? `These notes locked on ${lockDate.value} and can no longer be changed.`
    : `Only ${current.runByName}, who ran this check, can change its notes.`;
});

const sheet = computed<ConflictPrintSheet>(() => ({
  firmName: me.value?.company?.name ?? '',
  runByName: record.value?.runByName ?? '',
  runAt: record.value?.runAtUtc ?? null,
  saved: true,
  queryText: record.value?.queryText ?? '',
  parties: record.value?.parties ?? [],
  includedTimeEntries: record.value?.includedTimeEntries ?? false,
  hiddenRowCount: 0,
}));

const dirty = computed(
  () => uiState.editing && uiState.draft !== (record.value?.notes ?? ''),
);

watch(dirty, (next) => emit('dirty', next));

// A different record, or one that stopped offering an edit, ends any edit in progress.
watch(
  () => [record.value?.id, record.value?.notesEditable] as const,
  ([id, editable], [previousId]) => {
    if (id !== previousId || !editable) cancelEditing();
  },
);

function startEditing(): void {
  uiState.draft = record.value?.notes ?? '';
  uiState.editing = true;
}

function cancelEditing(): void {
  uiState.editing = false;
  uiState.draft = '';
}

async function saveNotes(): Promise<void> {
  try {
    await recordStore.saveNotes(uiState.draft);
    uiState.editing = false;
    handleSuccess('Notes saved.');
  } catch (error) {
    // A 403 is answered on the record itself; anything else keeps the draft to retry.
    if (!recordStore.notesRefusal) handleError(error);
  }
}

/** The page calls this before printing — a collapsed panel prints as nothing. */
function expandPartials(): void {
  if (resultList.value) resultList.value.expandPartials();
}

__expose({ expandPartials });

return (_ctx: any,_cache: any) => {
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(LlSecondaryButton, {
        "prepend-icon": "mdi-arrow-left",
        "data-cy": "conflict-saved-back",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('back')))
      }, {
        default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
          _createTextVNode(" Previous checks ", -1)
        ]))]),
        _: 1
      }),
      (_unref(record))
        ? (_openBlock(), _createBlock(LlSecondaryButton, {
            key: 0,
            class: "ml-auto",
            "data-cy": "conflict-saved-print",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('print')))
          }, {
            default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
              _createTextVNode(" Print ", -1)
            ]))]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    (_unref(recordStore).recordLoading)
      ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
          key: 0,
          class: "mt-4",
          type: "heading, paragraph, list-item-three-line@3",
          "data-cy": "conflict-saved-loading"
        }))
      : (_unref(recordStore).recordNotFound)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[7] || (_cache[7] = [
            _createElementVNode("span", { class: "font-medium" }, "This saved check could not be found.", -1),
            _createTextVNode(" It may belong to another firm, or the link may be wrong. ", -1)
          ]))]))
        : (_unref(recordStore).recordError)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _cache[9] || (_cache[9] = _createElementVNode("span", { class: "font-medium text-red-700" }, "The saved check could not be loaded.", -1)),
              _cache[10] || (_cache[10] = _createTextVNode(" Nothing is shown, so nothing here says what it found. ", -1)),
              _createElementVNode("div", _hoisted_5, [
                _createVNode(LlSecondaryButton, {
                  size: "small",
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('retry')))
                }, {
                  default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                    _createTextVNode(" Try again ", -1)
                  ]))]),
                  _: 1
                })
              ])
            ]))
          : (_unref(record))
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                _createVNode(ConflictPrintMasthead, { sheet: sheet.value }, null, 8, ["sheet"]),
                _createElementVNode("section", _hoisted_6, [
                  _createElementVNode("header", _hoisted_7, [
                    _createVNode(LlIcon, {
                      icon: "mdi-lock-outline",
                      size: "small",
                      color: "grey-darken-1"
                    }),
                    _cache[11] || (_cache[11] = _createElementVNode("h2", {
                      id: "conflict-saved-heading",
                      class: "text-sm font-medium text-gray-800"
                    }, " Saved check · results fixed ", -1)),
                    _createElementVNode("span", _hoisted_8, " Run by " + _toDisplayString(_unref(record).runByName) + " on " + _toDisplayString(runAt.value), 1),
                    _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(record).includedTimeEntries
                ? 'Time entries included'
                : 'Time entries not searched'), 1)
                  ]),
                  _createElementVNode("div", _hoisted_10, [
                    _createElementVNode("dl", _hoisted_11, [
                      _cache[12] || (_cache[12] = _createElementVNode("dt", { class: "text-gray-500" }, "Searched for", -1)),
                      _createElementVNode("dd", _hoisted_12, _toDisplayString(_unref(record).queryText), 1),
                      _createElementVNode("dt", _hoisted_13, _toDisplayString(_unref(record).parties.length) + " " + _toDisplayString(_unref(record).parties.length === 1 ? 'name' : 'names') + " checked ", 1),
                      _createElementVNode("dd", _hoisted_14, _toDisplayString(_unref(record).parties.join(' · ')), 1)
                    ]),
                    (_unref(record).result.truncated)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_15, " Only the first " + _toDisplayString(_unref(record).result.partiesChecked) + " of " + _toDisplayString(_unref(record).result.partiesFound) + " names were checked. ", 1))
                      : _createCommentVNode("", true),
                    (!_unref(recordStore).allRows.length)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                          _cache[13] || (_cache[13] = _createElementVNode("span", { class: "font-medium text-green-800" }, "No conflicts found.", -1)),
                          _createTextVNode(" Searched " + _toDisplayString(_unref(CONFLICT_SCOPE_SENTENCE)) + _toDisplayString(scopeTail.value) + " Invoice narratives are not searched. ", 1)
                        ]))
                      : (_openBlock(), _createBlock(ConflictResultList, {
                          key: 2,
                          ref_key: "resultList",
                          ref: resultList,
                          "full-matches": _unref(recordStore).fullMatches,
                          "partial-matches": _unref(recordStore).partialMatches,
                          onOpen: _cache[3] || (_cache[3] = ($event: any) => (emit('open', $event)))
                        }, null, 8, ["full-matches", "partial-matches"])),
                    (!_unref(record).notesEditable)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                          _cache[14] || (_cache[14] = _createElementVNode("h3", { class: "text-sm font-medium text-gray-800" }, "Notes", -1)),
                          (_unref(record).notes)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_18, _toDisplayString(_unref(record).notes), 1))
                            : (_openBlock(), _createElementBlock("p", _hoisted_19, " No notes were recorded. ")),
                          _createElementVNode("p", _hoisted_20, [
                            (_unref(recordStore).notesRefusal)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_21, "Your change was not saved."))
                              : _createCommentVNode("", true),
                            _createTextVNode(" " + _toDisplayString(fixedNotesReason.value), 1)
                          ])
                        ]))
                      : _createCommentVNode("", true)
                  ])
                ]),
                (_unref(record).notesEditable)
                  ? (_openBlock(), _createElementBlock("section", _hoisted_22, [
                      _createElementVNode("div", _hoisted_23, [
                        _cache[16] || (_cache[16] = _createElementVNode("h3", {
                          id: "conflict-saved-notes-heading",
                          class: "text-sm font-medium text-gray-800"
                        }, " Notes ", -1)),
                        _createElementVNode("span", _hoisted_24, " You can change these until " + _toDisplayString(lockDate.value) + ". After that they are fixed with the rest of the check. ", 1),
                        (!uiState.editing)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                              _createVNode(LlSecondaryButton, {
                                size: "small",
                                "prepend-icon": "mdi-pencil-outline",
                                "data-cy": "conflict-saved-notes-edit",
                                onClick: startEditing
                              }, {
                                default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                  _createTextVNode(" Edit notes ", -1)
                                ]))]),
                                _: 1
                              })
                            ]))
                          : _createCommentVNode("", true)
                      ]),
                      (uiState.editing)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_26, [
                            _createVNode(ConflictNotesField, {
                              id: "conflict-saved-notes-input",
                              modelValue: uiState.draft,
                              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((uiState.draft) = $event)),
                              disabled: _unref(recordStore).savingNotes
                            }, null, 8, ["modelValue", "disabled"]),
                            _createElementVNode("div", _hoisted_27, [
                              _createVNode(LlSecondaryButton, {
                                color: "primary",
                                loading: _unref(recordStore).savingNotes,
                                disabled: draftOverLimit.value,
                                "data-cy": "conflict-saved-notes-save",
                                onClick: saveNotes
                              }, {
                                default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                                  _createTextVNode(" Save notes ", -1)
                                ]))]),
                                _: 1
                              }, 8, ["loading", "disabled"]),
                              _createVNode(LlSecondaryButton, {
                                disabled: _unref(recordStore).savingNotes,
                                "data-cy": "conflict-saved-notes-cancel",
                                onClick: cancelEditing
                              }, {
                                default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                                  _createTextVNode(" Cancel ", -1)
                                ]))]),
                                _: 1
                              }, 8, ["disabled"])
                            ])
                          ]))
                        : _createCommentVNode("", true),
                      _createElementVNode("div", {
                        class: _normalizeClass(uiState.editing ? 'print-only' : '')
                      }, [
                        (_unref(record).notes)
                          ? (_openBlock(), _createElementBlock("p", _hoisted_28, _toDisplayString(_unref(record).notes), 1))
                          : (_openBlock(), _createElementBlock("p", _hoisted_29, " No notes were recorded. "))
                      ], 2)
                    ]))
                  : _createCommentVNode("", true)
              ], 64))
            : _createCommentVNode("", true)
  ]))
}
}

})